<script setup lang="ts">
import { ref, watch } from 'vue';
import { useElementSize } from '@vueuse/core';

import type { PriceRibbonMonth } from '../priceRibbon';

import PriceCalendarTileView from './PriceCalendarTileView.vue';
import PriceCalendarHeader from './PriceCalendarHeader.vue';
import type { PriceCalendarTile } from './models';

interface PriceCalendarProps {
  selectedMonth: string;
  months: PriceRibbonMonth[];
  priceCalendarTiles: PriceCalendarTile[][];
}

const props = defineProps<PriceCalendarProps>();

interface PriceCalendarEmits {
  (eventName: 'select-date', month: Date): void;
  (eventName: 'select-month', month: Date): void;
}

const emit = defineEmits<PriceCalendarEmits>();

const shouldShowMonthsSelector = ref<boolean>(false);
const priceCalendarRef = ref<HTMLDivElement | undefined>(undefined);
const { width: priceCalendarWidth, height: priceCalendarHeight } =
  useElementSize(priceCalendarRef);

function createTileKey(
  calendarRowIndex: number,
  calendarTileIndex: number
): string {
  return `${calendarRowIndex}-${calendarTileIndex}`;
}

function onSelectMonth(month: Date): void {
  emit('select-month', month);
  shouldShowMonthsSelector.value = false;
}

// TODO: This is first attempt on calendar responsivness, it will be improved in future iterations
watch([priceCalendarWidth, priceCalendarHeight], () => {
  const paddingThreshold = 480;
  const smallSidePaddingPx = 8;
  const largeSidePaddingPx = 48;
  const sidePadding =
    priceCalendarWidth.value < paddingThreshold
      ? smallSidePaddingPx
      : largeSidePaddingPx;
  const bottomPadding = 24;
  const tilesInRow = 7;
  const tilesInColumn = 6;
  const horizontalSpace = priceCalendarWidth.value - sidePadding;
  const verticalSpace = priceCalendarHeight.value - bottomPadding;
  const availableSpace = Math.min(horizontalSpace, verticalSpace);
  const tileWidthFit = `${Math.floor(availableSpace / tilesInRow)}px`;
  const tileHeightFit = `${Math.floor(availableSpace / tilesInColumn)}px`;

  const size = tileWidthFit < tileHeightFit ? tileWidthFit : tileHeightFit;
  if (priceCalendarRef.value) {
    priceCalendarRef.value.style.setProperty('--calendar-tile-size', size);
  }
});
</script>

<template>
  <div ref="priceCalendarRef" class="price-calendar">
    <div
      class="header-wrapper"
      :class="{ underline: !shouldShowMonthsSelector }"
    >
      <PriceCalendarHeader
        :should-show-months-selector="shouldShowMonthsSelector"
        :months="months"
        :selected-month-iso-date="props.selectedMonth"
        @select-month="onSelectMonth"
        @toggle-months-selector="
          shouldShowMonthsSelector = !shouldShowMonthsSelector
        "
      />
    </div>
    <div>
      <div
        v-for="(calendarRow, calendarRowIndex) of props.priceCalendarTiles"
        :key="calendarRowIndex"
        class="calendar-row"
      >
        <PriceCalendarTileView
          v-for="(calendarTile, calendarTileIndex) of calendarRow"
          :key="createTileKey(calendarRowIndex, calendarTileIndex)"
          :calendar-tile="calendarTile"
          @select-date="emit('select-date', $event)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.price-calendar {
  --calendar-tile-background-color: var(--color-white);
  --calendar-tile-border-radius: var(--border-radius-8);
  --calendar-tile-day-font-color: var(--color-secondary-40);
  --calendar-tile-day-font: var(--font-label-l-bold);
  --calendar-tile-hover-color: var(--color-secondary-97);
  --calendar-tile-info-icon-right: 6px;
  --calendar-tile-info-icon-top: 1px;
  --calendar-tile-price-font-color: var(--color-secondary-60);
  --calendar-tile-price-font: var(--font-label-s-light);
  // NOTE: This is initial value updated in watcher above
  --calendar-tile-size: 50px;
  --calendar-tile-gap: 0px;
  --calendar-tile-cheap-color: var(--color-positive-70);
  --calendar-tile-moderate-color: var(--color-tertiary-60);
  --calendar-tile-expensive-color: var(--color-negative-70);
  --calendar-weekday-font-color: var(--color-secondary-60);
  --calendar-weekday-font: var(--font-label-s-light);
  --calendar-price-mask-animation-delay: 0.2s;

  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;

  .header-wrapper {
    width: 100%;
    padding: var(--spacing-24);
    z-index: 100;
    position: relative;

    &.underline {
      border-bottom: 1px solid var(--color-secondary-90);
    }
  }

  .calendar-row {
    width: 100%;
    display: flex;
    gap: var(--calendar-tile-gap);
  }
}
</style>
